// Enterprise "Contact us" page (/contact). Left: pitch + direct contact details. Right: an inbound
// form that stores the enquiry server-side (POST /contact) so admins can read it in the admin panel.
import { useState } from "react";

import { trackContactFormSubmit } from "@/lib/analytics";
import { submitContact } from "@/lib/api";
import { useSeoMeta } from "@/lib/useSeoMeta";

const CONTACT_EMAIL = "contact@latentforce.ai";
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for the (currently hidden) phone row
const CONTACT_PHONE = "+91 8130391497";
const CONTACT_ADDRESS = "1st Floor, BNN Tower, 760, 16th Main Road, Banashankari Stage II, Bengaluru, Karnataka – 560070";

function MailIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for the (currently hidden) phone row
function PhoneIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>;
}
function PinIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>;
}

// Design-system eyebrow: orange dash + mono uppercase label.
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.15em]" style={{ color: "var(--text-muted)" }}>
      <span className="h-px w-5" style={{ background: "var(--brand)" }} />
      {children}
    </span>
  );
}

function ContactRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: "var(--glass)", border: "1px solid var(--border-light)", color: "var(--text-muted)" }}>{icon}</span>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.15em]" style={{ color: "var(--text-muted)" }}>{label}</p>
        <div className="mt-1 text-sm" style={{ color: "var(--text)" }}>{children}</div>
      </div>
    </div>
  );
}

export default function ContactPage() {
  useSeoMeta({ title: "Contact — SkillPatch", path: "/contact" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [rateLimited, setRateLimited] = useState(false);

  const ready = Boolean(name.trim() && email.trim() && message.trim());

  // Wrap a setter so typing after a send/error clears the status banner and re-enables submit.
  function edit(setter: (v: string) => void) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(e.target.value);
      if (sent) setSent(false);
      if (error) setError("");
      if (rateLimited) setRateLimited(false);
    };
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    setError("");
    setRateLimited(false);
    try {
      await submitContact({
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        message: message.trim(),
      });
      // Fired only on a successful POST — the form submits via fetch, so GA4's automatic
      // form_submit never sees it (which is why form_start had no matching submit).
      trackContactFormSubmit();
      setSent(true);
      // Clear the form so it doesn't look like the submission is still pending.
      setName("");
      setEmail("");
      setCompany("");
      setMessage("");
    } catch (err: any) {
      if (err?.status === 429) {
        setRateLimited(true);
      } else {
        setError(err?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const label = "font-mono text-[10px] uppercase tracking-[0.15em] mb-2 block";
  const field = "w-full rounded-xl px-3.5 py-3 text-sm outline-none transition";
  const fieldStyle: React.CSSProperties = { background: "var(--glass)", border: "1px solid var(--border)", color: "var(--text)" };

  return (
    <main className="mx-auto grid max-w-6xl gap-12 px-6 py-16 sm:px-10 lg:grid-cols-2 lg:gap-16 lg:py-24">
      {/* Left — pitch + direct contact */}
      <div>
        <Eyebrow>Contact Us</Eyebrow>
        <h1 className="mt-5 font-display font-normal tracking-tight" style={{ fontSize: "clamp(34px, 4.5vw, 52px)", lineHeight: 1.1, color: "var(--text)" }}>
          Questions, ideas, or<br />just want to <span className="text-primary">connect?</span>
        </h1>
        <p className="mt-5 max-w-md text-[15px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          Whether you&apos;re publishing a skill, have feedback, or want to connect to us on
          SkillPatch — drop us a note and we&apos;ll get back to you.
        </p>

        <div className="mt-10 flex flex-col gap-6">
          <ContactRow icon={<MailIcon />} label="Email us">
            <a href={`mailto:${CONTACT_EMAIL}`} className="transition hover:text-primary">{CONTACT_EMAIL}</a>
          </ContactRow>
          {/* Phone hidden from UI for now:
          <ContactRow icon={<PhoneIcon />} label="Call us">
            <a href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`} className="transition hover:text-primary">{CONTACT_PHONE}</a>
          </ContactRow>
          */}
          <ContactRow icon={<PinIcon />} label="Visit us">
            <span className="max-w-sm" style={{ color: "var(--text-secondary)" }}>{CONTACT_ADDRESS}</span>
          </ContactRow>
        </div>
      </div>

      {/* Right — inbound form */}
      <div className="rounded-2xl p-6 sm:p-8" style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={label} style={{ color: "var(--text-muted)" }} htmlFor="c-name">Name</label>
              <input id="c-name" required value={name} onChange={edit(setName)} placeholder="Your name" className={`${field} focus:border-primary`} style={fieldStyle} />
            </div>
            <div>
              <label className={label} style={{ color: "var(--text-muted)" }} htmlFor="c-email">Email</label>
              <input id="c-email" type="email" required value={email} onChange={edit(setEmail)} placeholder="your@email.com" className={`${field} focus:border-primary`} style={fieldStyle} />
            </div>
          </div>
          <div>
            <label className={label} style={{ color: "var(--text-muted)" }} htmlFor="c-company">Company (optional)</label>
            <input id="c-company" value={company} onChange={edit(setCompany)} placeholder="Your company name" className={`${field} focus:border-primary`} style={fieldStyle} />
          </div>
          <div>
            <label className={label} style={{ color: "var(--text-muted)" }} htmlFor="c-msg">What are you looking for?</label>
            <textarea id="c-msg" required rows={7} value={message} onChange={edit(setMessage)} placeholder="Please describe your requirements" className={`${field} resize-y leading-relaxed focus:border-primary`} style={fieldStyle} />
          </div>
          {sent && (
            <p className="text-sm text-emerald-500">Thanks — your message has been sent. We&apos;ll get back to you soon.</p>
          )}
          {rateLimited && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.08] px-3.5 py-2.5 text-sm text-amber-700 dark:text-amber-300">
              Thanks for reaching out! You&apos;ve already sent us a few messages today.
              Please try again tomorrow.
            </div>
          )}
          {error && (
            <p className="text-sm text-red-500">{error} You can also write to us at {CONTACT_EMAIL}.</p>
          )}
          <button
            type="submit"
            disabled={!ready || submitting}
            className="mt-1 flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 font-display text-sm font-semibold uppercase tracking-wide text-white shadow-lg shadow-primary/20 transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {submitting ? "Sending…" : "Send message"}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
          </button>
        </form>
      </div>
    </main>
  );
}
